import { useContext, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { PencilIcon } from './optionsIcons';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import { updateUrlLabel } from '../../services/monday';
import { FileContext } from '../../context/FileContext';

const DropdownContainer = styled.div`
  margin-right: 6px;
`;

const DropdownButton = styled.button`
  cursor: pointer;
  height: 16px;
  background-color: white;
  display: flex;
  justify-content: center;
  align-items: center;
  border: 1px solid #c5c7d0;
  font-size: 9px;
  color: #676879;
  font-weight: 600;
  border-radius: 3px;
  padding-left: 4px;
  padding-right: 4px;
  gap: 4px;

  &:hover {
    opacity: 0.8;
    background-color: rgb(247, 248, 249);
  }
  &.active {
    background-color: #0073ea;
    border: 1px solid #0073ea;
  }
`;

const DropdownContent = styled.div`
  margin-top: 8px;
  position: absolute;
  left: 0%;
  width: 100%;
  z-index: 1000;
  display: flex;
  justify-content: center;

  div {
    background-color: white;
    box-shadow: 0px 8px 16px 0px rgba(0, 0, 0, 0.2);
    border-radius: 5px;
    text-align: center;
    display: flex;
    flex-direction: column;
    padding: 10px;
    gap: 5px;

    div {
      padding: 0px;
      box-shadow: none;
      gap: 0px;
    }

    .info-text {
      font-size: 8px;
      text-align: left;
      line-height: 2;
    }
  }
`;

const CustomInput = styled.input`
  width: 120px;
  height: 26.66px;
  border-radius: 4px;
  border: 1px solid #c5c7d0;
  color: #676879;
  font-size: 10px;
  padding-left: 4px;
  ${({ readOnly }) =>
    readOnly ? 'cursor: not-allowed; background-color: #E4E9EE' : ''};

  :focus-visible {
    ${({ readOnly }) => (readOnly ? 'outline: none;' : '')};
  }
`;

const CustomButton = styled.button`
  width: 120px;
  height: 21.33px;
  border-radius: 4px;
  background: #0073ea;
  color: white;
  border: none;
  font-size: 10px;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  ${({ disabled }) => (disabled ? 'opacity: 0.6' : '')};
`;

const LinkEditor = ({ selectedItem, updateFormFields, addedFormFields }) => {
  const { t } = useTranslation();
  const { boardId, itemId } = useContext(FileContext);
  const [urlData, setUrlData] = useState({ url: '', text: '' });
  const [isOpen, setIsOpen] = useState(false);
  const [isValidUrl, setIsValidUrl] = useState(false);
  const readOnly = selectedItem.type === 'link';
  const dropdownRef = useRef(null);
  const dropdownButtonRef = useRef(null);

  const triggerDropdown = () => {
    if (selectedItem.urlData) {
      setUrlData(selectedItem.urlData);
      if (selectedItem.urlData.url) {
        setIsValidUrl(true);
      }
    }
    setIsOpen((s) => !s);
  };

  const urlPattern = /^(https?:\/\/|www\.)[\da-z.-]+\.[a-z.]{2,6}[/?#\S]*$/i;

  const updateUrlData = (e, key) => {
    e.stopPropagation();
    const value = e.target.value;
    if (key === 'url') {
      setIsValidUrl(!!urlPattern.test(value));
    }
    setUrlData((prev) => {
      return { ...prev, [key]: value };
    });
  };

  const { mutate, isLoading: isUpdatingLink } = useMutation(updateUrlLabel);

  const updateBoardData = (data) => {
    const columnId = selectedItem.itemId;
    const value = `${data.url} ${data.text}`;

    mutate(
      { boardId, itemId, columnId, value },
      {
        onError: () => {
          console.error('Error: Could not update text for url in board data.');
        },
      },
    );
  };

  const handleSave = () => {
    const updatedUrlData = {
      url: urlData.url,
      text: !urlData.text && urlData.url ? urlData.url : urlData.text,
    };

    selectedItem.type === 'link' &&
      selectedItem?.urlData?.text !== updatedUrlData.text &&
      updateBoardData(updatedUrlData);

    selectedItem.urlData = updatedUrlData;
    updateFormFields([...addedFormFields]);
    setIsOpen(false);
  };

  const handleClickOutside = (event) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target) &&
      dropdownButtonRef.current &&
      !dropdownButtonRef.current.contains(event.target)
    ) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener('click', handleClickOutside);

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (selectedItem.itemId === 'sd-link' && !selectedItem?.urlData?.url) {
      setIsOpen((s) => true);
    }
  }, [selectedItem]);

  return (
    <>
      <DropdownContainer>
        <DropdownButton ref={dropdownButtonRef} onClick={triggerDropdown}>
          <PencilIcon />
        </DropdownButton>
        {isOpen ? (
          <DropdownContent>
            <div ref={dropdownRef}>
              <CustomInput
                placeholder={t('Enter label')}
                value={urlData?.text}
                readOnly={false}
                onChange={(e) => updateUrlData(e, 'text')}
                onClick={(e) => e.stopPropagation()}
              />
              <div>
                <CustomInput
                  placeholder={t('Enter link')}
                  value={urlData?.url}
                  readOnly={readOnly}
                  onChange={(e) => updateUrlData(e, 'url')}
                  onClick={(e) => e.stopPropagation()}
                />
                <span className="info-text">
                  {t('Link should be a valid url.')}
                </span>
              </div>
              <CustomButton
                disabled={!isValidUrl || isUpdatingLink}
                onClick={handleSave}
              >
                {t('Save')}
              </CustomButton>
            </div>
          </DropdownContent>
        ) : null}
      </DropdownContainer>
    </>
  );
};

export default LinkEditor;
